import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Mail, CheckCircle2, Brain, Target, Zap, Clock, Quote, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedProgressChart from '../components/AnimatedProgressChart';
import QMGSymbols from '../components/QMGSymbols';
import { API } from '../App';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await API.post('/newsletter', { email });
      toast.success('Welcome! Check your inbox for mental golf tips.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/4398355/pexels-photo-4398355.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Golf course landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/80 via-[#1A1A1A]/50 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10 py-20">
          <div className="max-w-2xl">
            <div className="inline-block bg-white/15 backdrop-blur-sm px-4 py-1.5 mb-4 animate-fade-in-up">
              <span className="text-white text-xs font-bold uppercase tracking-[0.25em]">Golfers</span>
            </div>
            <p className="text-white/80 text-sm uppercase tracking-[0.2em] mb-6 animate-fade-in-up" data-testid="hero-golfer-statement">
              Performance coaching for golfers wanting to master their mental game and unlock their true potential on the course.
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-white font-medium leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Master Your Mind,<br />
              <span className="text-[#BC562B]">Own the Moment</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-lg animate-fade-in-up animation-delay-200">
              Tour-proven performance coaching to help you stay calm, focused, and confident on the course — no matter the pressure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link
                to="/contact"
                className="bg-[#BC562B] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-cta-btn"
              >
                Start Your Journey
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/human-first"
                className="border border-white/40 text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-human-first-btn"
              >
                The Common Thread
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/a3-assessment"
                className="text-white/85 px-4 py-4 text-sm font-semibold uppercase tracking-wider hover:text-white transition-all duration-300 flex items-center justify-center gap-2 underline underline-offset-8 decoration-[#BC562B]"
                data-testid="hero-a3-btn"
              >
                Take the A³ Assessment
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Coach Hero Section */}
      <section className="relative py-16 md:py-20 bg-[#1A2E1D] overflow-hidden" data-testid="hero-coach-section">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
        <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-block bg-[#BC562B]/20 border border-[#BC562B]/30 px-4 py-1.5 mb-4">
                <span className="text-[#BC562B] text-xs font-bold uppercase tracking-[0.25em]">Coaches</span>
              </div>
              <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-5" data-testid="hero-coach-statement">
                Built for coaches who want more than a spreadsheet — manage students, compare performance, and prove the value of your coaching.
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl text-white font-medium leading-tight">
                Elevate Your Coaching<br className="hidden md:block" />
                <span className="text-[#BC562B]">With Data That Matters</span>
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:min-w-[200px]">
              <Link
                to="/coach-login"
                className="border border-white/40 text-white px-7 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-coach-login-btn"
              >
                Coach Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QMG Blueprint Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="mac-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          {/* QMG Blueprint Image with Clickable Areas */}
          <div className="relative max-w-5xl mx-auto rounded-lg overflow-hidden shadow-lg">
            <img 
              src="https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/6nsg7siq_The%20QMG%20blueprint%20for%20mental%20focus.png" 
              alt="The QMG Blueprint - Mental movement under pressure" 
              className="w-full h-auto"
              data-testid="mac-blueprint-image"
            />
            <div className="absolute inset-0 flex">
              <Link to="/mac/mindful" className="w-1/3 h-full flex items-end justify-center pb-[8%] group" data-testid="mac-step-1">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore Awareness →
                </span>
              </Link>
              <Link to="/mac/accepting" className="w-1/3 h-full flex items-end justify-center pb-[8%] group" data-testid="mac-step-2">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore Acceptance →
                </span>
              </Link>
              <Link to="/mac/commitment" className="w-1/3 h-full flex items-end justify-center pb-[8%] group" data-testid="mac-step-3">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore Action →
                </span>
              </Link>
            </div>
          </div>

          <div className="flex justify-center mt-12">
            <QMGSymbols size="md" tone="dark" />
          </div>

          <div className="text-center mt-8">
            <Link
              to="/mac-research"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#2C5530] text-[#2C5530] font-semibold hover:bg-[#2C5530] hover:text-white transition-all duration-300"
              data-testid="mac-research-link"
            >
              <BookOpen className="w-4 h-4" />
              The Research Backing the QMG Blueprint
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QMG Quote Banner */}
      <section className="py-16 bg-[#2C5530] relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center relative z-10">
          <Quote className="w-10 h-10 text-white/20 mx-auto mb-5" />
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-4xl text-white font-medium leading-relaxed">
            "When your commitment to your personal values is greater than your addiction to the outcome, your golf will improve for the better."
          </blockquote>
          <p className="text-white/60 uppercase tracking-[0.2em] text-xs mt-6">
            On Intention vs Outcome
          </p>
        </div>
      </section>

      {/* Acceptance vs Resistance — Visual Teaser */}
      <section className="py-24 md:py-32 bg-white" data-testid="acceptance-vs-resistance-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-[#BC562B] text-sm uppercase tracking-[0.2em] mb-4">Two Minds, One Shot</p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-6">
              Acceptance vs. Resistance
            </h2>
            <p className="text-[#57534E] text-lg leading-relaxed">
              Every golfer hits bad shots. What separates them is what happens in the 30 seconds after. See the two states of mind side by side — and learn which one you've been feeding.
            </p>
            <div className="flex justify-center mt-8">
              <QMGSymbols size="md" tone="dark" />
            </div>
          </div>

          <Link
            to="/mac/acceptance-vs-resistance"
            className="group block max-w-3xl mx-auto overflow-hidden bg-[#0E1A10] hover:shadow-2xl transition-all duration-500"
            data-testid="landing-card-acceptance-vs-resistance"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/5x0g3fj6_AAA.png"
                alt="Non-Acceptance vs. Acceptance — the two states of mind that decide your next shot"
                className="w-full h-auto object-contain group-hover:scale-[1.01] transition-transform duration-700"
                data-testid="landing-acceptance-vs-resistance-image"
              />
            </div>
          </Link>

          <div className="text-center mt-12">
            <Link
              to="/mac/acceptance-vs-resistance"
              className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300"
              data-testid="landing-cta-acceptance-vs-resistance"
            >
              Explore the Difference
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Two Wolves Quote Banner */}
      <section className="py-20 bg-[#2C5530] relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center relative z-10">
          <Quote className="w-12 h-12 text-white/20 mx-auto mb-6" />
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-4xl text-white font-medium leading-relaxed mb-6">
            "There are 2 wolves. They are always fighting. One is darkness and despair, the other is light and happiness. Which wolf will win over time? …Whichever one you choose to feed."
          </blockquote>
          <p className="text-white/70 uppercase tracking-[0.2em] text-sm">
            The Two Wolves
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FAFAF9]" data-testid="four-areas-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-10">
            <p className="text-[#BC562B] text-sm uppercase tracking-[0.2em] mb-4">The Approach</p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-6">
              Four Key Areas for Balanced Improvement
            </h2>
            <p className="text-[#57534E] text-lg leading-relaxed max-w-2xl mx-auto">
              Reduce the gap between where you are now and where you want to be by focusing attention and planning across these interconnected areas.
            </p>
          </div>

          {/* Mind Factor Image with Clickable Areas */}
          <div className="relative max-w-5xl mx-auto rounded-lg overflow-hidden shadow-lg">
            <img 
              src="https://customer-assets.emergentagent.com/job_ce3ebfde-a92e-4c8a-aabd-1eead688aaee/artifacts/s0q6fq8n_Mind%20Factor.png" 
              alt="The Mind Factor - The 4 key areas to train in golf" 
              className="w-full h-auto"
              data-testid="mind-factor-landing"
            />
            <div className="absolute inset-0 flex">
              <Link to="/the-approach/before" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="area-before">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore Before →
                </span>
              </Link>
              <Link to="/the-approach/during" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="area-during">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore During →
                </span>
              </Link>
              <Link to="/the-approach/in-between" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="area-in-between">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore In-Between →
                </span>
              </Link>
              <Link to="/the-approach/after" className="w-1/4 h-full flex items-end justify-center pb-[8%] group" data-testid="area-after">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#2C5530] text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  Explore After →
                </span>
              </Link>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/the-approach"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#1A1A1A] text-[#1A1A1A] font-semibold hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
              data-testid="approach-overview-link"
            >
              <BookOpen className="w-4 h-4" />
              The Full Approach
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-[#2C5530] relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center relative z-10">
          <Quote className="w-12 h-12 text-white/20 mx-auto mb-6" />
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-4xl text-white font-medium leading-relaxed mb-6">
            "Being in the moment gives you the best chance to move through it."
          </blockquote>
          <p className="text-white/70 uppercase tracking-[0.2em] text-sm">
            Quiet Mind Golf Philosophy
          </p>
        </div>
      </section>

      {/* Client Portal CTA — hidden until final details are updated
      <section className="py-24 md:py-32 bg-[#F2F0E9]" data-testid="portal-cta-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#BC562B] text-sm uppercase tracking-[0.2em] mb-4">Client Portal</p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-6">
                Track Your Mental Game Progress
              </h2>
              <p className="text-[#57534E] text-lg leading-relaxed mb-8">
                Access your personal dashboard to log sessions, track game statistics, maintain a training diary, and visualize your improvement over time.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Log coaching and practice sessions',
                  'Track game statistics and mental scores',
                  'Maintain training and tournament diaries',
                  'View progress reports and trends'
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#57534E]">
                    <CheckCircle2 className="w-5 h-5 text-[#2C5530] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300"
                  data-testid="portal-register-btn"
                >
                  Create Your Account
                  <ChevronRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex items-center gap-2 border border-[#2C5530] text-[#2C5530] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-all duration-300"
                  data-testid="portal-features-btn"
                >
                  See All Features
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <AnimatedProgressChart />
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-[#1A1A1A]" data-testid="newsletter-section">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl text-center">
          <Mail className="w-12 h-12 text-[#BC562B] mx-auto mb-6" />
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white font-medium mb-4">
            Mental Golf Tips Straight to Your Inbox
          </h2>
          <p className="text-white/60 mb-8">
            Join golfers improving their mental game with practical tips, insights, and strategies.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/50 px-6 py-4 focus:border-[#BC562B] focus:ring-0"
              data-testid="newsletter-email-input"
              required
            />
            <button
              type="submit"
              disabled={subscribing}
              className="bg-[#BC562B] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-all duration-300 disabled:opacity-50"
              data-testid="newsletter-submit-btn"
            >
              {subscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </section>

      {/* Control Quote Banner */}
      <section className="py-16 bg-[#2C5530] relative overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center relative z-10">
          <Quote className="w-10 h-10 text-white/20 mx-auto mb-5" />
          <blockquote className="font-['Playfair_Display'] text-2xl md:text-4xl text-white font-medium leading-relaxed">
            "Anything that is not in your control should not be in your mind."
          </blockquote>
          <p className="text-white/60 uppercase tracking-[0.2em] text-xs mt-6">
            On Focus & Control
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-white" data-testid="final-cta-section">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1A1A1A] mb-6">
            Ready to Transform Your Mental Game?
          </h2>
          <p className="text-[#57534E] text-lg mb-10 max-w-2xl mx-auto">
            Whether you're an amateur looking to enjoy golf more or a professional seeking that competitive edge, 
            the journey to a quieter mind starts with a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#BC562B] text-white px-10 py-5 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="final-cta-btn"
            >
              Book a Free Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/about"
              className="border border-[#2C5530] text-[#2C5530] px-10 py-5 text-sm font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-all duration-300"
              data-testid="final-secondary-btn"
            >
              About Scott Ritchie
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
